import PropTypes from 'prop-types';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
// material
import { Box, Typography, Divider } from '@mui/material';
import { useDispatch } from '../../redux/store';
// redux
import { getMessages, selectActiveMessages, selectConversations } from '../../redux/slices/internalChat';
// sub-components
import InternalChatHeader from './InternalChatHeader';
import InternalChatMessageList from './InternalChatMessageList';
import InternalChatInput from './InternalChatInput';

// ----------------------------------------------------------------------

InternalChatWindow.propTypes = {
  chatId: PropTypes.string.isRequired,
};

export default function InternalChatWindow({ chatId }) {
  const dispatch = useDispatch();

  const activeChatMessages = useSelector(selectActiveMessages);
  const conversations = useSelector(selectConversations);
  const presence = useSelector((state) => state.internalChat.presence.userStatusById);

  const [replyingTo, setReplyingTo] = useState(null);

  const [displayLimit, setDisplayLimit] = useState(50);

  // Reset na paginação ao mudar de conversa
  useEffect(() => {
    setDisplayLimit(50);
  }, [chatId]);

  // Rola e destaca a mensagem fixada ao clicar no banner
  const handleScrollToMessage = useCallback(
    (messageId) => {
      const index = activeChatMessages.findIndex((m) => String(m._id) === String(messageId));
      if (index !== -1) {
        const distanceFromEnd = activeChatMessages.length - index;
        if (distanceFromEnd > displayLimit) {
          setDisplayLimit(distanceFromEnd + 10); // Expande a quantidade de mensagens na tela p/ englobar a antiga
        }

        // Aguarda o render e rola
        setTimeout(() => {
          const el = document.getElementById(`msg-${messageId}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Highlight temporário
            el.style.transition = 'background-color 0.3s';
            el.style.backgroundColor = 'rgba(var(--mui-palette-primary-mainChannel) / 0.12)';
            setTimeout(() => {
              el.style.backgroundColor = '';
            }, 2000);
          }
        }, 100);
      }
    },
    [activeChatMessages, displayLimit]
  );

  const chat = useMemo(() => conversations.find((c) => c._id === chatId), [conversations, chatId]);

  // Busca o conteúdo completo da mensagem fixada (se estiver carregada no Redux)
  const pinnedMessageContent = useMemo(() => {
    if (!chat?.pinnedMessage?.messageId) return null;
    return activeChatMessages.find((m) => String(m._id) === String(chat.pinnedMessage.messageId)) || null;
  }, [chat, activeChatMessages]);

  const fetchedRefs = useRef(new Set());

  useEffect(() => {
    if (chatId && !fetchedRefs.current.has(chatId)) {
      dispatch(getMessages(chatId));
      fetchedRefs.current.add(chatId);
    }
  }, [dispatch, chatId]);

  // Listeners controlados pelo middleware global
  // useEffect(() => { ... }) removido

  if (!chatId || !chat) {
    return (
      <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" color="textSecondary">
          Selecione uma conversa para começar
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <InternalChatHeader
        chat={chat}
        presence={presence}
        pinnedMessageContent={pinnedMessageContent}
        onScrollToMessage={handleScrollToMessage}
      />

      <InternalChatMessageList
        messages={activeChatMessages}
        chat={chat}
        onReply={setReplyingTo}
        displayLimit={displayLimit}
        onLoadMore={() => setDisplayLimit((prev) => prev + 50)}
      />

      <Divider />

      <InternalChatInput
        chatId={chatId}
        chatMode={chat.mode}
        chatOwner={chat.ownerId?._id || chat.ownerId}
        participants={chat.participants}
        replyingTo={replyingTo}
        onCancelReply={() => setReplyingTo(null)}
      />
    </Box>
  );
}
